/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;
const libEntityDisplayName = require('./_EntityDisplayName.js');

// NOTE: This entity selector does not support Tabular Data at this time.
class MaterialsSelectorDisplayInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	getSelectInputHTMLID(pInputHTMLID)
	{
		return `#MATERIALS-ENTITY-DISPLAY-FOR-${pInputHTMLID}`;
	}

	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#MATERIALS-ENTITY-DISPLAY-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#MATERIALS-ENTITY-DISPLAY-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean}
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, null, pInput, pValue, pHTMLSelector, pRowIndex).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * @param {Record<string, any>} pInput
	 */
	_getEntity(pInput)
	{
		return (pInput?.PictForm?.Entity) || this.options.Entity || null;
	}

	/**
	 * The display name for a resolved entity, matching the PSP PickerDisplay contract so the old and new
	 * providers render identically through the migration:
	 *   - a present resolved name passes through,
	 *   - a resolved-but-blank name -> `<entity label> #<record ID>` (`Contractor #3456`),
	 *   - an unresolved/orphan id -> the raw stored value (orphan visibility), never an "Unnamed ..." label.
	 *
	 * @param {any} pDisplayName - The resolved display name from Select2Util.getEntityWithDisplayAsync.
	 * @param {Record<string, any>} pEntity - The raw entity record (has `.Name`, never `.DisplayName`).
	 * @param {Object} pInput - The input descriptor.
	 * @param {any} pValue - The raw stored value (id) for the orphan fallback.
	 * @return {string}
	 */
	_resolveDisplayNameHTML(pDisplayName, pEntity, pInput, pValue)
	{
		if (pDisplayName !== undefined && pDisplayName !== null && String(pDisplayName).trim() !== '')
		{
			return String(pDisplayName);
		}
		if (pEntity && pEntity[`ID${ this._getEntity(pInput) }`])
		{
			return libEntityDisplayName.unnamedEntityLabel(this._getEntity(pInput), pEntity[`ID${ this._getEntity(pInput) }`], pInput?.Name);
		}
		// Nothing resolved (orphan id) -> show the raw value so the bad reference stays visible.
		return String(pValue);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} [pRowIndex] - The row index parameter.
	 * @return {Promise<boolean>}
	 */
	async _onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		// Tabular Print/Read renders a static `...TABULAR-DATA-...` div (no select2 dropdown), so target that
		// via getTabularSelectInputID. The `...TABULAR-DROPDOWN-...` id is never rendered here, so using it
		// silently bailed and left the raw ID the base marshal wrote into the cell.
		const inputMethod = pRowIndex != null ? 'getTabularSelectInputID' : 'getSelectInputHTMLID';
		// Try to get the input element before we do anything else
		const tmpInputSelectElements = this.pict.ContentAssignment.getElement(this[inputMethod](pInput.Macro.RawHTMLID, pRowIndex));
		if (!tmpInputSelectElements || tmpInputSelectElements.length < 1)
		{
			return false;
		}
		/** @type {HTMLSelectElement} */
		const tmpInputSelectElement = tmpInputSelectElements[0];

		// if multiple, pValue will be a comma-separated list of IDs
		if (pInput.PictForm && pInput.PictForm.Multiple && typeof(pValue) === 'string')
		{
			const tmpValues = pValue.split(',');
			const tmpDisplayValues = [];
			for (const tmpValue of tmpValues)
			{
				if (tmpValue)
				{
					const { Record: tmpEntity, DisplayName: tmpDisplayName } = await this.pict.providers.Select2Util.getEntityWithDisplayAsync(this._getEntity(pInput), tmpValue,
					{
						JoinEntity: pInput.PictForm.JoinEntity,
						JoinEntityFirst: pInput.PictForm.JoinEntityFirst ?? true,
						JoinField: pInput.PictForm.JoinField ? pInput.PictForm.JoinField : pInput.PictForm.JoinEntity ? `ID${ pInput.PictForm.JoinEntity }` : null,
						EntityDisplayField: pInput.PictForm.EntityDisplayField,
						JoinEntityDisplayField: pInput.PictForm.JoinEntityDisplayField,
						Disabled: pInput.PictForm.Disabled || false,
						EntityTagLast: pInput.PictForm.EntityTag && pInput.PictForm.EntityTagLast,
					});
					// if we have an EntityTag, display that as a tag along with the Name
					if (pInput.PictForm.EntityTag)
					{
						let isDateTag = false;
						let tmpEntityTag = pInput.PictForm.EntityTag;
						if (pInput.PictForm.EntityTag.includes(':date'))
						{
							// update the EntityTag to remove the :date for display purposes
							tmpEntityTag = tmpEntityTag.replace(':date', '');
							isDateTag = true;
						}
						let tmpDisplayTemplate = '';
						if (tmpInputSelectElement.tagName === 'DIV')
						{
							tmpDisplayTemplate = this.pict.providers.Select2Util.buildSelect2CompoundDisplayTemplate(
								isDateTag ? this.pict.Dates.dayJS(tmpEntity[tmpEntityTag]).format('YYYY-MM-DD') : tmpEntity[tmpEntityTag] || 'N/A',
								this._resolveDisplayNameHTML(tmpDisplayName, tmpEntity, pInput, tmpValue),
								true,
								tmpEntity.EntityTagLast,
							);
						}
						else
						{
							tmpDisplayTemplate = tmpValue || (`Unnamed ${ this._getEntity(pInput) } #` + tmpEntity[`ID${ this._getEntity(pInput) }`]);
						}
						tmpDisplayValues.push(tmpDisplayTemplate);
					}
					else
					{
						tmpDisplayValues.push(this._resolveDisplayNameHTML(tmpDisplayName, tmpEntity, pInput, tmpValue));
					}
				}
			}
			// make the container look like a textarea for multiple values
			this.pict.ContentAssignment.addClass(tmpInputSelectElement, 'textarea');
			this.pict.ContentAssignment.removeClass(tmpInputSelectElement, 'input');
			this.pict.ContentAssignment.assignContent(tmpInputSelectElement, tmpDisplayValues.join(', '));
			return;
		}
		// for single value
		if (pValue)
		{
			const { Record: tmpEntity, DisplayName: tmpDisplayName } = await this.pict.providers.Select2Util.getEntityWithDisplayAsync(this._getEntity(pInput), pValue,
			{
				JoinEntity: pInput.PictForm.JoinEntity,
				JoinEntityFirst: pInput.PictForm.JoinEntityFirst ?? true,
				JoinField: pInput.PictForm.JoinField ? pInput.PictForm.JoinField : pInput.PictForm.JoinEntity ? `ID${ pInput.PictForm.JoinEntity }` : null,
				EntityDisplayField: pInput.PictForm.EntityDisplayField,
				JoinEntityDisplayField: pInput.PictForm.JoinEntityDisplayField,
				Disabled: pInput.PictForm.Disabled || false,
				EntityTagLast: pInput.PictForm.EntityTag && pInput.PictForm.EntityTagLast,
			});
			// if we have an EntityTag, display that as a tag along with the Name
			if (pInput.PictForm.EntityTag)
			{
				let isDateTag = false;
				let tmpEntityTag = pInput.PictForm.EntityTag;
				if (pInput.PictForm.EntityTag.includes(':date'))
				{
					// update the EntityTag to remove the :date for display purposes
					tmpEntityTag = tmpEntityTag.replace(':date', '');
					isDateTag = true;
				}
				let tmpDisplayTemplate = '';
				if (tmpInputSelectElement.tagName === 'DIV')
				{
					tmpDisplayTemplate = this.pict.providers.Select2Util.buildSelect2CompoundDisplayTemplate(
						isDateTag ? this.pict.Dates.dayJS(tmpEntity[tmpEntityTag]).format('YYYY-MM-DD') : tmpEntity[tmpEntityTag] || 'N/A',
						this._resolveDisplayNameHTML(tmpDisplayName, tmpEntity, pInput, pValue),
						false,
						tmpEntity.EntityTagLast,
					);
				}
				else
				{
					tmpDisplayTemplate = pValue || (`Unnamed ${ this._getEntity(pInput) } #` + tmpEntity[`ID${ this._getEntity(pInput) }`]);
				}
				this.pict.ContentAssignment.assignContent(tmpInputSelectElement, tmpDisplayTemplate);
			}
			else
			{
				const displayString = this._resolveDisplayNameHTML(tmpDisplayName, tmpEntity, pInput, pValue);
				this.pict.ContentAssignment.assignContent(tmpInputSelectElement, displayString);
			}
		}
		else
		{
			this.pict.ContentAssignment.assignContent(tmpInputSelectElement, '');
		}
	}
}

module.exports = MaterialsSelectorDisplayInputHandler;
